// components/waba-utm.js
// Simple UTM → CSV → Cookies (no cache)
// Includes special case for chatgpt.com (param OR referrer) with CSV lookup

/* ==================== Utils ==================== */
const DAY = 86400;
const nowSec = () => Math.floor(Date.now() / 1000);

/** Normalization (parity with the original PHP) */
function normalizeKey(str) {
  if (typeof str !== "string") return "";
  let s = str.trim().replace(/^["']|["']$/g, "");
  s = s.replace(/\+/g, " ");             // PLUS → spasi
  s = s.replace(/\u00A0/g, " ");         // NBSP → space
  s = s.replace(/[\u2013\u2014]/g, "-"); // en/em dash → '-'
  s = s.replace(/\s*\|\s*/g, "|");       // " a | b " → "a|b"
  s = s.replace(/[_\-]+/g, " ");         // '_' & '-' → space
  s = s.replace(/\s+/g, " ");            // compress spaces
  return s.toLowerCase().trim();
}

/* ==================== Cookies (preserve expiry) ==================== */
function getCookie(name) {
  const m = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") +
        "=([^;]*)"
    )
  );
  return m ? decodeURIComponent(m[1]) : "";
}

function getCookieDomain() {
  const host = (location.host || "").replace(/:\d+$/, "");
  if (!host || host === "localhost" || /^[\d.:]+$/.test(host)) return "";
  const labels = host.toLowerCase().split(".");
  if (labels.length < 2) return "";
  const last = labels[labels.length - 1];
  const prev = labels[labels.length - 2];
  let take = 2;
  if (last === "id" && labels.length >= 3) {
    const common = new Set([
      "co",
      "com",
      "net",
      "org",
      "ac",
      "sch",
      "web",
      "or",
      "my",
      "go"
    ]);
    if (common.has(prev)) take = 3;
  }
  return "." + labels.slice(-take).join(".");
}

function deleteCookieVariants(name) {
  const host = (location.host || "").replace(/:\d+$/, "");
  const root = getCookieDomain();
  const targets = [];
  if (host) targets.push(host);
  if (root) targets.push(root);
  ["", "_exp"].forEach((sfx) => {
    targets.forEach((dom) => {
      const base = `${encodeURIComponent(
        name + sfx
      )}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
      if (dom === host) {
        document.cookie =
          base + (location.protocol === "https:" ? "; Secure" : "");
      } else {
        document.cookie =
          base +
          `; Domain=${dom}` +
          (location.protocol === "https:" ? "; Secure" : "");
      }
    });
  });
}

function setCookiePreserveExpiry(name, value, days = 31) {
  if (!value) return;
  const expName = name + "_exp";
  const root = getCookieDomain();
  const secure = location.protocol === "https:";
  const firstSet = !getCookie(name);

  // Avoid host-only vs root clashes
  deleteCookieVariants(name);

  let base = `${encodeURIComponent(name)}=${value}; Path=/; SameSite=Lax`;
  if (root) base += `; Domain=${root}`;
  if (secure) base += "; Secure";

  if (firstSet) {
    const expireTs = nowSec() + Math.max(1, days) * DAY;
    document.cookie =
      base + `; Expires=${new Date(expireTs * 1000).toUTCString()}`;

    let marker = `${encodeURIComponent(expName)}=${String(
      expireTs
    )}; Path=/; SameSite=Lax`;
    if (root) marker += `; Domain=${root}`;
    if (secure) marker += "; Secure";
    marker += `; Expires=${new Date(expireTs * 1000).toUTCString()}`;
    document.cookie = marker;
    return;
  }

  const origExpTs = parseInt(getCookie(expName), 10) || 0;
  if (origExpTs > nowSec()) {
    document.cookie =
      base + `; Expires=${new Date(origExpTs * 1000).toUTCString()}`;
  } else {
    // session cookie if marker missing/expired
    document.cookie = base;
  }
}

/* ==================== CSV (auto-delimiter, no cache) ==================== */
function stripBOM(s) {
  return s && s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}

function detectDelimiter(firstLine) {
  const cands = [",", ";", "\t", "|"];
  let best = ",";
  let max = 0;
  for (const d of cands) {
    const c = firstLine.split(d).length - 1;
    if (c > max) {
      max = c;
      best = d;
    }
  }
  return best;
}

function csvToRows(text) {
  const lines = stripBOM(String(text || "")).split(/\r\n|\n|\r/);
  if (!lines.length) return [];
  const delim = detectDelimiter(lines[0] || "");
  const rows = [];
  for (const line of lines) {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQ = !inQ;
        }
      } else if (ch === delim && !inQ) {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    if (out.length === 1 && out[0].trim() === "") continue;
    rows.push(out);
  }
  return rows;
}

/** Build map { normalized_name: code } */
function buildMapFromCsv(contents) {
  const rows = csvToRows(contents);
  const map = {};
  if (!rows.length) return map;

  // Skip header heuristic
  let start = 0;
  const first0 = (rows[0][0] || "").toString().trim().toLowerCase();
  if (
    first0.includes("name") ||
    first0.includes("campaign") ||
    first0.includes("medium")
  ) {
    start = 1;
  }

  for (let i = start; i < rows.length; i++) {
    const r = rows[i];
    if (!r || r.length < 2) continue;
    const key = normalizeKey(String(r[0]));
    const val = String(r[1]).trim();
    if (key && val) map[key] = val;
  }
  return map;
}

async function fetchCsvMap(url) {
  if (!url) return {};
  const res = await fetch(url, { cache: "no-store", credentials: "omit" });
  if (!res.ok) return {};
  const text = await res.text();
  return buildMapFromCsv(text);
}

/* ==================== Channel detection ==================== */
function detectChannel() {
  const ref = document.referrer || "";
  if (!ref) return "organic direct";
  try {
    const hostRef = new URL(ref).host;
    const hostCur = location.host;
    if (hostRef && hostCur && hostRef.includes(hostCur)) {
      return "organic direct";
    }
    if (
      /\b(google|bing|yahoo|duckduckgo|baidu|yandex)\./i.test(
        hostRef
      )
    ) {
      return "organic search";
    }
    if (/chatgpt\.com/i.test(hostRef)) return "chatgpt"; // treat specially upstream
  } catch {}
  return "organic direct";
}

/* ==================== Main API ==================== */
/**
 * @param {Object} opts
 *  - csv: { mediumUrl, campaignUrl }
 *  - ttlDays: number (31)
 *  - channelCodes: {
 *       medium:   { 'organic direct': 'J7O', 'organic search': 'J6O' },
 *       campaign: { 'organic direct'?: '...', 'organic search'?: '...', 'default': 'MJ' }
 *    }
 *  - debug: boolean
 */
export async function initWabaTracking(opts = {}) {
  const {
    csv = {},
    ttlDays = 31,
    channelCodes = {
      medium: { "organic direct": "J7O", "organic search": "J6O" },
      campaign: { default: "MJ" }
    },
    debug = false
  } = opts;

  const params = new URLSearchParams(window.location.search);
  let utm_source = params.get("utm_source") || "";
  let utm_medium = params.get("utm_medium") || "";
  let utm_campaign = params.get("utm_campaign") || "";

  // Read existing UTM cookies
  const cookieUtmSource = getCookie("utm_source") || "";
  const cookieUtmMedium = getCookie("utm_medium") || "";
  const cookieUtmCampaign = getCookie("utm_campaign") || "";

  const firstVisit =
    !getCookie("waba_code") && !getCookie("wabac_code");
  const chDetected = detectChannel(); // 'organic search' | 'organic direct' | 'chatgpt'
  const isSearchHit = chDetected === "organic search";
  const isDirectHit = chDetected === "organic direct";
  const isChatgptRef = chDetected === "chatgpt";
  const isChatgptUtm =
    utm_source && normalizeKey(utm_source) === "chatgpt.com";
  const hasUtm = Boolean(utm_source || utm_medium || utm_campaign);

  // Helper: apakah utm_source sudah kategori "website"/non-paid
  function isWebsiteSource(v) {
    const n = normalizeKey(v);
    if (!n) return false;
    return n === "website" || n === "(not set)" || n === "(none)";
  }

  // 🔹 Paid → Organic override HANYA untuk organic search tanpa UTM
  const isOrganicSearchNoUtm = !hasUtm && isSearchHit;
  const paidToOrganic =
    isOrganicSearchNoUtm && !isWebsiteSource(cookieUtmSource);

  // Only update on:
  // - first visit
  // - ada UTM di URL
  // - organic search (tanpa UTM → boleh override)
  // - chatgpt ref / utm
  // - atau transisi paid → organic search (paidToOrganic)
  //
  // NOTE:
  // - Organic direct TANPA firstVisit TIDAK masuk kriteria, jadi cookie lama dipertahankan.
  const shouldUpdate =
    firstVisit ||
    hasUtm ||
    isSearchHit ||
    isChatgptRef ||
    isChatgptUtm ||
    paidToOrganic;

  if (!shouldUpdate) {
    if (debug) {
      console.log("[waba] keep cookies (no update)", {
        chDetected,
        hasUtm,
        paidToOrganic,
        cookieUtmSource,
        cookieUtmMedium,
        cookieUtmCampaign
      });
    }
    return {
      code: getCookie("waba_code") || "",
      ccode: getCookie("wabac_code") || "",
      updated: false,
      source: "none"
    };
  }

  let waba_code = "";
  let wabac_code = "";
  let source = "";

  // === Special Case: ChatGPT (utm_source OR referrer) ===
  if (isChatgptUtm || isChatgptRef) {
    source = "chatgpt";

    // Override raw UTM as requested
    utm_source = "website";
    utm_medium = "chatgpt";
    utm_campaign = "default";

    // Save raw UTM (preserve expiry)
    setCookiePreserveExpiry("utm_source", utm_source, ttlDays);
    setCookiePreserveExpiry("utm_medium", utm_medium, ttlDays);
    setCookiePreserveExpiry("utm_campaign", utm_campaign, ttlDays);

    // Perform CSV lookup using the OVERRIDDEN values
    const [mapMedium, mapCampaign] = await Promise.all([
      fetchCsvMap(csv.mediumUrl),
      fetchCsvMap(csv.campaignUrl)
    ]);

    const keyMedium = normalizeKey(utm_medium); // 'chatgpt'
    const keyCamp = normalizeKey(utm_campaign); // 'default'

    waba_code = keyMedium
      ? mapMedium[keyMedium] || mapMedium["default"] || ""
      : mapMedium["default"] || "";
    wabac_code = keyCamp
      ? mapCampaign[keyCamp] || mapCampaign["default"] || ""
      : mapCampaign["default"] || "";

    // If CSV still empty, fallback to channelCodes based on direct (safe default)
    if (!waba_code || !wabac_code) {
      const ch = isSearchHit ? "organic search" : "organic direct";
      waba_code =
        waba_code || (channelCodes.medium?.[ch] || "");
      wabac_code =
        wabac_code ||
        (channelCodes.campaign?.["default"] || "");
    }
  }

  // === General UTM flow (non-ChatGPT) ===
  else if (hasUtm) {
    source = "utm";

    // Save raw utm as-is
    if (utm_source)
      setCookiePreserveExpiry("utm_source", utm_source, ttlDays);
    if (utm_medium)
      setCookiePreserveExpiry("utm_medium", utm_medium, ttlDays);
    if (utm_campaign)
      setCookiePreserveExpiry(
        "utm_campaign",
        utm_campaign,
        ttlDays
      );

    const [mapMedium, mapCampaign] = await Promise.all([
      fetchCsvMap(csv.mediumUrl),
      fetchCsvMap(csv.campaignUrl)
    ]);

    const keyMedium = normalizeKey(utm_medium);
    const keyCamp = normalizeKey(utm_campaign);

    waba_code = keyMedium
      ? mapMedium[keyMedium] || mapMedium["default"] || ""
      : mapMedium["default"] || "";
    wabac_code = keyCamp
      ? mapCampaign[keyCamp] || mapCampaign["default"] || ""
      : mapCampaign["default"] || "";

    // If mapping fails, fallback to channel
    if (!waba_code || !wabac_code) {
      const ch = isSearchHit ? "organic search" : "organic direct";
      waba_code =
        waba_code || (channelCodes.medium?.[ch] || "");
      wabac_code =
        wabac_code ||
        (channelCodes.campaign?.["default"] || "");
      source = ch;
    }
  }

  // === No UTM: channel fallback ===
  else if (isSearchHit) {
    // Organic search (tanpa UTM) → boleh override (termasuk paid→organic)
    source = "website";
    waba_code =
      channelCodes.medium?.["organic search"] || "";
    wabac_code =
      channelCodes.campaign?.["organic search"] ||
      channelCodes.campaign?.["default"] ||
      "";

    // Hardcode raw utm cookies for organic search
    setCookiePreserveExpiry(
      "utm_source",
      "website",
      ttlDays
    );
    setCookiePreserveExpiry(
      "utm_medium",
      "organic search",
      ttlDays
    );
    setCookiePreserveExpiry(
      "utm_campaign",
      "default",
      ttlDays
    );
  } else {
    // Organic direct
    // NOTE:
    // - Branch ini hanya akan dijalankan kalau shouldUpdate = true.
    // - Untuk direct TANPA firstVisit & TANPA UTM, shouldUpdate = false
    //   → cookie lama TIDAK diubah (sesuai requirement).
    source = "website";
    waba_code =
      channelCodes.medium?.["organic direct"] || "";
    wabac_code =
      channelCodes.campaign?.["organic direct"] ||
      channelCodes.campaign?.["default"] ||
      "";

    // Hardcode raw utm cookies for organic direct
    setCookiePreserveExpiry(
      "utm_source",
      "website",
      ttlDays
    );
    setCookiePreserveExpiry(
      "utm_medium",
      "organic direct",
      ttlDays
    );
    setCookiePreserveExpiry(
      "utm_campaign",
      "default",
      ttlDays
    );
  }

  // Set code cookies (preserve expiry)
  if (waba_code)
    setCookiePreserveExpiry("waba_code", waba_code, ttlDays);
  if (wabac_code)
    setCookiePreserveExpiry(
      "wabac_code",
      wabac_code,
      ttlDays
    );

  if (debug) {
    console.log(
      "[waba] event:",
      source,
      "firstVisit:",
      firstVisit,
      "hasUtm:",
      hasUtm,
      "isSearchHit:",
      isSearchHit,
      "isDirectHit:",
      isDirectHit,
      "isChatgptRef:",
      isChatgptRef,
      "isChatgptUtm:",
      isChatgptUtm,
      "paidToOrganic:",
      paidToOrganic
    );
    console.log("[waba] codes:", { waba_code, wabac_code });
  }

  return {
    code: waba_code,
    ccode: wabac_code,
    updated: true,
    source
  };
}
